import React, { useState } from "react";
import { UserRoundPen, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KEY = "yalola.name_nudge_later";
export const nameNudgeDismissed = () => { try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; } };

// Lot 532 — « Corrige ton nom » : le nom du compte ressemble à « AA AA » / « azerty » / « Test User ». Non bloquant :
// la carte revient à chaque session tant que le nom n'est pas corrigé (users.name_issue côté /auth/me).
export const NameNudgeCard = ({ onClose }) => {
  const { user, t, refreshMe } = useApp();
  const parts = (user?.name || "").trim().split(/\s+/);
  const [first, setFirst] = useState(user?.first_name || parts[0] || "");
  const [last, setLast] = useState(user?.last_name || parts.slice(1).join(" ") || "");
  const [busy, setBusy] = useState(false);

  const later = () => { try { sessionStorage.setItem(KEY, "1"); } catch { /* private mode */ } onClose(); };
  const save = async () => {
    const fn = first.trim(); const ln = last.trim();
    if (!fn || !ln) { toast.error(t("profile_edit.err_name", "Prénom et nom sont requis")); return; }
    setBusy(true);
    try {
      await api.patch("/users/me", { first_name: fn, last_name: ln, name: `${fn} ${ln}` });
      await refreshMe();
      toast.success(t("name_nudge.saved", "Nom mis à jour ✓"));
      onClose();
    } catch (e) { toast.error(e?.friendlyMessage || e?.message || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  return (
    <div className="yalola-card p-4 relative animate-in fade-in slide-in-from-top-2 duration-500" data-testid="name-nudge-card">
      <button type="button" onClick={later} className="absolute top-3 right-3 p-1 rounded-full hover:opacity-70" aria-label={t("name_nudge.later", "Plus tard")} data-testid="name-nudge-close">
        <X size={16} style={{ color: "var(--yalola-text-muted)" }} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="h-11 w-11 shrink-0 rounded-full grid place-items-center" style={{ background: "#FEF3C7", color: "#92400E" }}><UserRoundPen size={20} /></div>
        <div className="min-w-0">
          <div className="font-bold font-display" data-testid="name-nudge-title">{t("name_nudge.title", "Corrige ton nom")}</div>
          <p className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="name-nudge-desc">
            {t("name_nudge.desc", "« {name} » ne ressemble pas à un vrai nom. Indique ton prénom et ton nom pour que les autres membres te reconnaissent.").replace("{name}", user?.name || "")}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Input value={first} onChange={(e) => setFirst(e.target.value)} placeholder={t("profile_edit.first_name", "Prénom")} aria-label={t("profile_edit.first_name", "Prénom")} className="rounded-full h-10" data-testid="name-nudge-first" />
        <Input value={last} onChange={(e) => setLast(e.target.value)} placeholder={t("profile_edit.last_name", "Nom")} aria-label={t("profile_edit.last_name", "Nom")} className="rounded-full h-10" data-testid="name-nudge-last" />
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" className="rounded-full" onClick={later} disabled={busy} data-testid="name-nudge-later">{t("name_nudge.later", "Plus tard")}</Button>
        <Button size="sm" className="rounded-full pill-primary" onClick={save} disabled={busy} data-testid="name-nudge-save">{t("name_nudge.save", "Enregistrer")}</Button>
      </div>
    </div>
  );
};

export default NameNudgeCard;
